/**
 * CollectionFilterBar
 *
 * Minimal Web Component for the visual filter bar.
 * - Checkbox toggles (shape, setting style, setting profile, band style):
 *   handled here in JS via explicit click + preventDefault, so CSS overlays
 *   on the :checked state can't block the unchecking click.
 * - Carat range pills are radios that drive two hidden inputs
 *   (filter.p.m.<key>.gte / .lte) which is what facets.js serializes.
 * - Carousel arrows wire up here so they re-bind after AJAX DOM swaps.
 * - URL sync on load so the active pill stays highlighted after navigation —
 *   even when Shopify doesn't return the filter group (empty result set).
 */
class CollectionFilterBar extends HTMLElement {
  connectedCallback() {
    this.syncCheckboxesFromUrl();
    this.initCheckboxToggles();
    this.initSlotSizing();
    this.initCarousels();
    this.initCaratRanges();
  }

  /**
   * Sync checkbox state with the URL. Source of truth: the URL params.
   *
   * BIDIRECTIONAL: this is an absolute assignment, not a conditional `true`
   * set. A checkbox is checked IFF its value appears in the URL for its
   * filter name. This is critical because the server-rendered HTML can drift
   * out of sync from the URL in two cases:
   *   1. A filter value present in the URL but DROPPED by collection.filters
   *      (Shopify hides values with 0 matching products in the current set).
   *      Without this sync, the pill would render unchecked even though the
   *      filter is active in the URL.
   *   2. A checkbox that was natively toggled by the user click but then
   *      AJAX-swapped back in with a stale `checked` attribute — the new
   *      HTML's `checked` attribute would persist incorrectly. Absolute
   *      assignment forces it to match the URL.
   *
   * (Dawn doesn't have this function — it relies entirely on server HTML.
   * Our visual filter bar uses hardcoded pill values that may not 1:1 with
   * collection.filters, so we need this defensive layer.)
   */
  syncCheckboxesFromUrl() {
    const url = new URLSearchParams(window.location.search);
    this.querySelectorAll('input[type="checkbox"][name][value]').forEach((cb) => {
      const values = url.getAll(cb.name);
      cb.checked = values.includes(cb.value);
    });
  }

  /**
   * Immediate visual feedback when any filter checkbox/radio changes.
   *
   * Native <label>→<input> toggle handles state flipping; native `input` and
   * `change` events fire automatically and reach facets.js's debounced
   * listener on the form. We only need to add the loading class up-front so
   * the skeleton appears immediately on click — without this, the user would
   * stare at the unchanged grid for the debounce window (~100ms) and the
   * subsequent fetch (~300ms), making clicks feel unresponsive.
   *
   * Overlay spans (.cfb-shape-image etc.) use `pointer-events: none` so the
   * native toggle path is reliable across browsers.
   */
  initCheckboxToggles() {
    this.addEventListener('change', () => {
      document.querySelector('#ProductGridContainer .collection')?.classList.add('loading');
    });
  }

  /**
   * Fit a whole number of item slots into every group.
   *
   * The four groups sit in equal grid columns, but a column is rarely an exact
   * multiple of one item, so the leftover pixels used to render as a sliced
   * half-item at the right edge of each group — and each group sliced at a
   * different point, which is what made the bar look uneven.
   *
   * Here we take the narrowest column, subtract the arrow gutters, and work out
   * how many items of at least --cfb-slot fit. That count is then divided back
   * into the column to get --cfb-slot-size: the exact item width at which those
   * items fill the column edge to edge. Every group shows the same number of
   * items, at the same width, starting and ending on the same pixel.
   *
   * Mobile is exempt: it scrolls the full column width with native touch.
   */
  initSlotSizing() {
    const row = this.querySelector('.cfb-settings-row');
    if (!row) return;

    const desktop = window.matchMedia('(min-width: 768px)');

    const sync = () => {
      if (!desktop.matches) {
        this.style.removeProperty('--cfb-visible');
        this.style.removeProperty('--cfb-slot-size');
        return;
      }

      const styles = getComputedStyle(this);
      const minSlot = parseFloat(styles.getPropertyValue('--cfb-slot')) || 68;
      const gap = parseFloat(styles.getPropertyValue('--cfb-slot-gap')) || 8;
      const gutter = parseFloat(styles.getPropertyValue('--cfb-gutter')) || 24;

      const columns = Array.from(row.children).map((group) => group.clientWidth);
      if (!columns.length) return;

      const available = Math.min.apply(null, columns) - gutter * 2;
      if (available <= 0) return;

      const visible = Math.max(1, Math.floor((available + gap) / (minSlot + gap)));
      // Floor to whole pixels so rounding can never push the row a fraction
      // past its container and register as overflow.
      const size = Math.floor((available - gap * (visible - 1)) / visible);

      // setProperty needs a string for custom properties — a raw number is
      // silently ignored in Chrome, leaving the CSS default in place.
      this.style.setProperty('--cfb-visible', String(visible));
      this.style.setProperty('--cfb-slot-size', size + 'px');
    };

    sync();
    // Re-measure once the icons and webfont have landed — the first pass runs
    // before they affect layout.
    window.addEventListener('load', sync, { once: true });
    window.addEventListener('resize', sync);
    desktop.addEventListener('change', sync);
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(sync).observe(row);
  }

  /**
   * Wire up the Shape, Setting Style, and Band Style carousels
   * (prev/next arrows + scroll state).
   */
  initCarousels() {
    this.querySelectorAll('.cfb-carousel-wrap').forEach((wrap) => {
      const scrollEl = wrap.querySelector(
        '.cfb-shapes-scroll-port, .cfb-shapes-row, .cfb-settings-icons--wrap'
      );
      if (!scrollEl) return;

      const prev = wrap.querySelector('[data-cfb-arrow="prev"]');
      const next = wrap.querySelector('[data-cfb-arrow="next"]');

      const updateArrows = () => {
        // Arrows only make sense when the items overflow their viewport.
        // When everything fits (e.g. <= 4 band styles) hide them entirely
        // via a class the CSS reacts to, rather than leaving them disabled.
        // Tolerance absorbs the few pixels of slack left by rounding slot
        // widths down, so a group whose items exactly fit doesn't show arrows.
        const overflowing = scrollEl.scrollWidth > scrollEl.clientWidth + 4;
        wrap.classList.toggle('cfb-carousel-wrap--no-overflow', !overflowing);
        if (prev) prev.disabled = scrollEl.scrollLeft <= 0;
        if (next) next.disabled = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 4;
      };

      prev?.addEventListener('click', () => {
        scrollEl.scrollBy({ left: -getScrollStep(scrollEl), behavior: 'smooth' });
      });
      next?.addEventListener('click', () => {
        scrollEl.scrollBy({ left: getScrollStep(scrollEl), behavior: 'smooth' });
      });
      scrollEl.addEventListener('scroll', updateArrows, { passive: true });

      // Each group's width now comes from its (fluid) grid column, so whether
      // it overflows depends on the viewport and on late-loading icons — a
      // single measurement at connect time runs before layout settles and
      // wrongly hides the arrows. Measure now, then again whenever the size
      // can have changed.
      updateArrows();
      window.addEventListener('load', updateArrows, { once: true });
      window.addEventListener('resize', updateArrows);
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(updateArrows).observe(scrollEl);
    });
  }

  /**
   * Carat range buckets — multi-value submission.
   *
   * Each bucket pill (.cfb-carat-item, a <div role="button">) wraps a set of
   * hidden checkbox inputs, one per discrete carat value that falls in that
   * bucket's range. Clicking the pill toggles ALL hidden inputs at once, and
   * FormData submits them as multiple `filter.p.m.custom.carat=X` URL params
   * (which Shopify OR-matches).
   *
   * The bucket MUST be a <div>, not a <label>: a <label> containing multiple
   * inputs auto-toggles its FIRST nested input on click, which fights this
   * toggle-all logic and leaves a single value re-checked on unselect.
   *
   * Buckets are mutually exclusive: clicking one clears the others. Buckets
   * with no values get .cfb-carat-item--empty and become unclickable.
   */
  initCaratRanges() {
    const items = this.querySelectorAll('.cfb-carat-item');
    if (!items.length) return;

    const self = this;

    items.forEach((item) => {
      const values = item.querySelectorAll('.cfb-carat-bucket-value');
      const hasValues = values.length > 0;
      const anyChecked = Array.from(values).some((v) => v.checked);

      item.classList.toggle('is-active', anyChecked);
      if (!hasValues) item.classList.add('cfb-carat-item--empty');
    });

    const handleBucketActivation = (item) => {
      if (item.classList.contains('cfb-carat-item--empty')) return;

      const wasActive = item.classList.contains('is-active');

      // Mutual exclusion: clear all buckets
      items.forEach((other) => {
        other.querySelectorAll('.cfb-carat-bucket-value').forEach((v) => (v.checked = false));
        other.classList.remove('is-active');
      });

      // Toggle: if not active, activate it; if active, leave deactivated
      if (!wasActive) {
        item.querySelectorAll('.cfb-carat-bucket-value').forEach((v) => (v.checked = true));
        item.classList.add('is-active');
      }

      // Trigger facets.js submission
      const trigger = self.querySelector('.cfb-carat-bucket-value') || item;
      trigger.dispatchEvent(new Event('input', { bubbles: true }));
    };

    items.forEach((item) => {
      item.addEventListener('click', () => handleBucketActivation(item));
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleBucketActivation(item);
        }
      });
    });
  }
}

/**
 * Compute how far a carousel should scroll per arrow click.
 *
 * @param {HTMLElement} scrollEl - The scrollable element
 *   (cfb-shapes-scroll-port, cfb-shapes-row, or cfb-settings-icons--wrap)
 * @returns {number} - Pixel distance to scroll on each click
 */
function getScrollStep(scrollEl) {
  const item = scrollEl.querySelector('.cfb-shape-item, .cfb-setting-item, .cfb-band-item');
  if (!item) return scrollEl.clientWidth * 0.8;

  // Step by whole slots so a click never leaves an item half in view. The gap
  // is read off the item's own flex row — for Shape that's .cfb-shapes-row
  // inside the scroller, not the scroller itself.
  const gap = parseFloat(getComputedStyle(item.parentElement).columnGap) || 0;
  const stride = item.getBoundingClientRect().width + gap;
  if (!stride) return scrollEl.clientWidth * 0.8;

  return Math.max(1, Math.round(scrollEl.clientWidth / stride)) * stride;
}

customElements.define('collection-filter-bar', CollectionFilterBar);